import React, { useState, useEffect } from "react";
import { Box, TextField, InputAdornment, IconButton } from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import errorSign from "../../assets/images/errorSign.svg";
import { resetPassword } from "../../Actions/ResetPasswordAction";
import checkImg from "../../assets/images/check_circle.svg";

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    temporaryPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    temp: false,
    new: false,
    confirm: false,
  });
  const [formError, setFormError] = useState("");
  const [showError, setShowError] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    mix: false,
    special: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [successMessage, setSuccessMessage] = useState(false);
  const dispatch = useDispatch();
  const { success, error } = useSelector((state) => state.resetPassword);

  useEffect(() => {
    if (formData.newPassword) {
      validatePasswordStrength(formData.newPassword);
    }
  }, [formData.newPassword]);

  useEffect(() => {
    if (success) {
      resetFormState();
    } else if (error) {
      handleError(error);
    }
  }, [success, error]);
useEffect(() => {
  let timer;

  if (showError) {
    timer = setTimeout(() => {
      setShowError(false);
      setFormError("");
    }, 5000);
  }

  return () => {
    if (timer) clearTimeout(timer);
  };
}, [showError]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Safer password strength validator (still uses static regex now)
  const validatePasswordStrength = (password) => {
    const hasLength = password.length >= 8;

    // Static regex for lowercase letters – safe, no backtracking risk
    const hasLower = /[a-z]/.test(password);

    // Static regex for uppercase letters – safe
    const hasUpper = /[A-Z]/.test(password);

    // SonarSafe: This regex is static, simple, and not built from user input. No ReDoS risk.
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const hasMix = hasLower && hasUpper;

    setPasswordStrength({
      length: hasLength,
      mix: hasMix,
      special: hasSpecial,
    });
  };

  //console.log(formError, showError, 'formError, formError')
  const validateForm = () => {
    if (!formData.temporaryPassword)
      return setError("Please enter your temporary password.");
    if (!formData.newPassword)
      return setError("Please enter your new password.");
    if (!isPasswordStrong())
      return setError("Password does not meet strength requirements.");
    if (!formData.confirmNewPassword)
      return setError("Please confirm your new password.");
    if (formData.newPassword !== formData.confirmNewPassword) {
      setPasswordsMatch(false);
      return setError("Passwords do not match.");
    }
    setPasswordsMatch(true);
    return true;
  };

  const isPasswordStrong = () =>
    passwordStrength.length && passwordStrength.mix && passwordStrength.special;

  const setError = (msg) => {
    setFormError(msg);
    setShowError(true);
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const token = new URLSearchParams(window.location.search).get(
      "passwordToken"
    );
    dispatch(resetPassword(formData, token));
  };

  const resetFormState = () => {
    setSuccessMessage(true);
    setFormData({
      temporaryPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setFormError("");
    setShowError(false);
    setTimeout(() => {
      document.getElementById("btnforLoginPage").focus();
    }, 200);
  };

  const handleError = (error) => {
    const message = error || "An error occurred";
    setFormError(message);
    setShowError(true);
  };

  const renderPasswordField = (id, label, value, show, toggle, error) => {
    const errorId = `${id}-error`;

    return (
      <TextField
        id={id}
        name={id}
        label={label}
        type={show ? "text" : "password"}
        variant="outlined"
        onChange={handleInputChange}
        data-testid={id}
        value={value}
        error={!!formError && showError && error}
        aria-describedby={
          !!formError && showError && error ? errorId : undefined
        }
        InputLabelProps={{ shrink: true, style: { transform: "inherit" } }}
        InputProps={{
          style: { borderRadius: "4px" },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={toggle}
                edge="end"
                aria-label={show ? "Hide password" : "Show password"}
                // title={show ? "Hide password" : "Show password"}
              >
                {show ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        // helperText={
        //   !!formError && showError && error ? (
        //     <span id={errorId} role="alert">
        //       {formError}
        //     </span>
        //   ) : null
        // }
      />
    );
  };

  return (
    <>
      {showError && formError !== "" && (
        <div
          className="error_wraper"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <img src={errorSign} alt="" />
          {formError}
        </div>
      )}
      <div
        className="right-section forgotsection resetPassword"
        aria-labelledby="reset-password-heading"
      >
        {!successMessage ? (
          <div className="login-form">
            <h1 id="reset-password-heading">Reset your password</h1>
            <Box
              component="form"
              noValidate
              autoComplete="off"
              className="field"
              aria-describedby="password-guidelines"
            >
              {renderPasswordField(
                "temporaryPassword",
                <>
                  Temporary Password <span style={{ color: "red" }}>*</span>
                </>,
                formData.temporaryPassword,
                showPassword.temp,
                () => togglePasswordVisibility("temp"),
                true
              )}
              {renderPasswordField(
                "newPassword",
                <>
                  New Password <span style={{ color: "red" }}>*</span>
                </>,
                formData.newPassword,
                showPassword.new,
                () => togglePasswordVisibility("new"),
                true
              )}
              {renderPasswordField(
                "confirmNewPassword",
                <>
                  Confirm New Password <span style={{ color: "red" }}>*</span>
                </>,
                formData.confirmNewPassword,
                showPassword.confirm,
                () => togglePasswordVisibility("confirm"),
                !passwordsMatch
              )}
            </Box>
            <div
              className="password-strength"
              id="password-guidelines"
              aria-live="polite"
            >
              <p>Password strength:</p>
              <ul
                style={{
                  paddingLeft: 0,
                  marginTop: "5px",
                  listStyleType: "none",
                }}
              >
                <li aria-live="polite" aria-atomic="true">
                  <div
                    className={passwordStrength.length ? "valid" : "invalid"}
                  >
                    {passwordStrength.length ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    At least 8 characters
                  </div>
                </li>
                <li aria-live="polite" aria-atomic="true">
                  <div className={passwordStrength.mix ? "valid" : "invalid"}>
                    {passwordStrength.mix ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    Upper and lowercase letters
                  </div>
                </li>
                <li aria-live="polite" aria-atomic="true">
                  <div
                    className={passwordStrength.special ? "valid" : "invalid"}
                  >
                    {passwordStrength.special ? (
                      <CheckCircleOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment met"
                      />
                    ) : (
                      <ErrorOutline
                        aria-hidden="false"
                        role="img"
                        aria-label="Requirment not met"
                      />
                    )}{" "}
                    Special character
                  </div>
                </li>
              </ul>
            </div>
            <div className="profile_wrapperbtn">
              <button
                type="submit"
                data-testid="save"
                className="btnSubmit save"
                onClick={handleSubmit}
                aria-label="Save"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div
            className="emailsent-wrapper"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <img src={checkImg} alt="" />
            <h1 id="reset-sent-heading">Reset</h1>
            <p id="reset-successfully">Your password is successfully reset</p>
            <a
              href="/"
              className="btnSubmit"
              aria-label="login"
              id="btnforLoginPage"
              role="button"
            >
              Login
            </a>
          </div>
        )}
      </div>
    </>
  );
};

export default ResetPassword;
